import type { ContentTypeDefinition } from '../../lib/flotiq-client/types/api-response';
import type { HookOptions } from './hook-builders/types';

import { useMemo } from 'react';

import { getCtdFeaturedImage } from '../../lib/flotiq-client/api-helpers';

import { useContentObjects, useContentTypes } from '.';

export type HookFeaturedImagesData = Record<string, string>;

interface FeaturedImagesHookObjectResult {
  images: HookFeaturedImagesData;
  isLoading: boolean;
  errors: any;
  reload: () => void;
}

export type FeaturedImagesArrayResult = [
  images: HookFeaturedImagesData,
  isLoading: boolean,
  errors: any,
  reload: () => void,
];

export type FeaturedImagesHookResult = FeaturedImagesHookObjectResult &
  FeaturedImagesArrayResult;

const DEFAULT_OPTIONS = {};

/**
 * Get featured images dictionary where the key is content type name and the value is featured media URL
 */
export const useFeaturedImages = (
  contentTypes: string[] | ContentTypeDefinition[],
  options: HookOptions = DEFAULT_OPTIONS,
) => {
  const filteredTypes = useMemo(
    () => contentTypes?.filter((ctd) => ctd) || [],
    [contentTypes],
  );

  const contentTypeStrings = useMemo(
    () => filteredTypes.filter((ctd) => typeof ctd === 'string') || [],
    [filteredTypes],
  );

  const contentTypesByNameOptions = useMemo(
    () => [
      {
        names: contentTypeStrings,
        limit: contentTypeStrings.length,
      },
      {
        ...options,
        pause: options?.pause || !contentTypeStrings.length,
      },
    ],
    [contentTypeStrings, options],
  );

  const {
    data: contentTypesByName,
    isLoading: ctdsLoading,
    errors: ctdsErrors,
    reload,
  } = useContentTypes(...contentTypesByNameOptions);

  const contentTypesDict = useMemo(() => {
    const apiContentTypesDict = (contentTypesByName || []).reduce(
      (acc, ctd) => {
        acc[ctd.name] = ctd;
        return acc;
      },
      {} as { [key: string]: ContentTypeDefinition },
    );

    return filteredTypes.reduce(
      (acc, ctd) => {
        const contentTypeData =
          typeof ctd === 'string'
            ? apiContentTypesDict[ctd] || { name: ctd }
            : ctd;

        acc[contentTypeData.name] = {
          contentTypeData,
          featuredMediaId:
            contentTypeData?.featuredImage?.[0]?.dataUrl?.match(/[^/]+$/)?.[0],
        };
        return acc;
      },
      {} as {
        [key: string]: {
          contentTypeData: ContentTypeDefinition;
          featuredMediaId?: string;
        };
      },
    );
  }, [contentTypesByName, filteredTypes]);

  const imageIds = useMemo(
    () =>
      [
        ...new Set(
          Object.values(contentTypesDict || {}).map(
            ({ featuredMediaId }) => featuredMediaId,
          ),
        ),
      ].filter((id) => id),
    [contentTypesDict],
  );

  const mediaParams = useMemo(
    () => [
      {
        ids: imageIds,
        limit: imageIds.length,
      },
      { ...options, pause: options?.pause || !imageIds.length },
    ],
    [imageIds, options],
  );

  const {
    data: mediaByIds,
    isLoading: mediaLoading,
    errors: mediaErrors,
  } = useContentObjects('_media', ...mediaParams);

  const imagesDict = useMemo(
    () =>
      mediaByIds.reduce(
        (acc, media) => {
          acc[media.id] = media;
          return acc;
        },
        {} as Record<string, any>,
      ),
    [mediaByIds],
  );

  const images = useMemo(
    () =>
      Object.entries(contentTypesDict).reduce(
        (acc, [contentTypeName, { featuredMediaId }]) => {
          acc[contentTypeName] = getCtdFeaturedImage(
            contentTypeName,
            featuredMediaId ? imagesDict[featuredMediaId] : undefined,
          );
          return acc;
        },
        {} as Record<string, string>,
      ),
    [contentTypesDict, imagesDict],
  );

  const isLoading =
    (ctdsLoading && contentTypeStrings.length > 0) ||
    (mediaLoading && imageIds.length > 0);

  const errors = useMemo(() => {
    const err = [];
    if (mediaErrors) err.push(mediaErrors);
    if (ctdsErrors) err.push(ctdsErrors);
    return err.length ? err : null;
  }, [ctdsErrors, mediaErrors]);

  const result = [images, isLoading, errors, reload];
  Object.assign(result, {
    images,
    isLoading,
    errors,
    reload,
  });
  return result as FeaturedImagesHookResult;
};
